/**
 * CurrencyContext
 * Global currency state with localStorage persistence.
 * Provides currency code, symbol, and position config for formatting.
 */
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type CurrencyCode = 'MAD' | 'EUR' | 'USD';

export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  symbolPosition: 'prefix' | 'suffix';
  /** Exchange rate FROM MAD (base currency) TO this currency. MAD=1. */
  rateFromMAD: number;
}

const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  MAD: { code: 'MAD', symbol: 'MAD', symbolPosition: 'prefix', rateFromMAD: 1 },
  EUR: { code: 'EUR', symbol: '€', symbolPosition: 'suffix', rateFromMAD: 0.092 },
  USD: { code: 'USD', symbol: '$', symbolPosition: 'prefix', rateFromMAD: 0.10 },
};

const STORAGE_KEY = 'iris_currency';
const DEFAULT_CURRENCY: CurrencyCode = 'MAD';

const getStoredCurrency = (): CurrencyCode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (stored === 'MAD' || stored === 'EUR' || stored === 'USD')) {
      return stored as CurrencyCode;
    }
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_CURRENCY;
};

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  currencyConfig: CurrencyConfig;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(getStoredCurrency);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const value: CurrencyContextValue = {
    currency,
    setCurrency,
    currencyConfig: CURRENCY_CONFIGS[currency],
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextValue => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return ctx;
};

export { CURRENCY_CONFIGS };
